import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { toast } from "react-toastify"
import { SerializedError } from "@reduxjs/toolkit"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query"
import classNames from "classnames"
import styled from "styled-components"
import { v4 as uuidv4 } from "uuid"

import {
  useLazyValidateWHDeviceForPackingQuery,
  usePackWHManagerOrderItemMutation,
} from "store/api/warehouseManagerApi"
import { ApiTypes, UITypes } from "types"
import { ReactComponent as ExportIcon } from "assets/svg/export-icon.svg"
import { ReactComponent as IconPacking } from "assets/svg/modal-icon-packing.svg"

import getErrorMessage from "utils/getErrorMessage"
import { isValidDeviceSn } from "utils/isValidDeviceSn"
import { ModalLayout } from "layouts/ModalLayout"
import { ModalHeader } from "components/common/ModalHeader"
import { ButtonNew } from "components/form/ButtonNew"
import { InputSN } from "components/form/InputSN"

import { CWAPackingHandler } from "./CWAPackingHandler"
import { exportPackingDevices } from "./exportPackingDevices"

import styles from "./index.module.scss"

const ExportButton = styled(ButtonNew)`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ExportIconStyled = styled(ExportIcon)`
  flex-shrink: 0;
  width: 16px;
  height: 16px;

  path {
    stroke: currentColor;
  }
`

interface Props {
  order: ApiTypes.Model.WHManagerOrderNew
  onCancel: () => void
  onSuccess: () => void
}

export const PackingDeviceModal = ({ order, onCancel, onSuccess }: Props) => {
  const [packWHManagerOrderItem, { isLoading: isPackingCWA }] = usePackWHManagerOrderItemMutation()
  const [triggerValidateWHDeviceForPacking] = useLazyValidateWHDeviceForPackingQuery()
  const [validatedDevices, setValidatedDevices] = useState<UITypes.InvalidDevice[]>([
    {
      uid: uuidv4(),
      sn: "",
      errorMessage: "",
    },
  ])

  useEffect(() => {
    // This hack is used to fix the issue with the auto focus on the first input
    setTimeout(() => {
      setValidatedDevices([
        {
          uid: uuidv4(),
          sn: "",
          errorMessage: "",
        },
      ])
    }, 0)
  }, [])

  const orderItem = order.items[0]
  const isCWA = orderItem?.product_type === "cwa"

  const onItemDelete = (id: string) => {
    const result = validatedDevices?.filter(item => item.uid !== id)
    setValidatedDevices(result)
  }

  const checkDuplicates = (devices: UITypes.InvalidDevice[], currentItem: UITypes.InvalidDevice) => {
    const snCount: Record<string, number> = {}

    devices.forEach(item => {
      if (item.sn && item.uid !== currentItem.uid) {
        snCount[item.sn] = (snCount[item.sn] || 0) + 1
      }
    })

    const isDuplicate = currentItem.sn && snCount[currentItem.sn] > 0

    return { ...currentItem, errorMessage: isDuplicate ? `Duplicate sn: ${currentItem.sn}` : "" }
  }

  const handleSetValidatedDevices = (data: UITypes.InvalidDevice, index: number) => {
    setValidatedDevices(prev => {
      const updated = prev.map(item =>
        item.uid === data.uid ? { ...item, sn: data.sn, errorMessage: data.errorMessage } : item,
      )

      return [
        ...updated,
        ...(index === prev.length - 1 && updated.length < Number(orderItem?.quantity)
          ? [
              {
                uid: uuidv4(),
                sn: "",
                errorMessage: "",
              },
            ]
          : []),
      ]
    })
  }

  const handleBlur = async (data: UITypes.InvalidDevice, index: number) => {
    const trimmedSn = data.sn.trim()
    if (!trimmedSn || !isValidDeviceSn(trimmedSn)) {
      return
    }

    const updatedDevices = validatedDevices.map(item => (item.uid === data.uid ? { ...item, sn: trimmedSn } : item))
    const currentItem = updatedDevices.find(item => item.uid === data.uid)

    if (currentItem) {
      const validatedItem = checkDuplicates(updatedDevices, { uid: data.uid, sn: trimmedSn })

      if (validatedItem.errorMessage) {
        handleSetValidatedDevices(validatedItem, index)
        return
      }
    }

    handleSetValidatedDevices({ uid: data.uid, sn: trimmedSn, errorMessage: "" }, index)

    try {
      const result = await triggerValidateWHDeviceForPacking(trimmedSn)

      if (result.error) {
        const errorMessage = getErrorMessage(result.error)
        handleSetValidatedDevices(
          { uid: data.uid, sn: trimmedSn, errorMessage: errorMessage || "Device validation failed" },
          index,
        )
        return
      }

      if (result.data?.value) {
        handleSetValidatedDevices({ uid: data.uid, sn: trimmedSn, errorMessage: "" }, index)
      } else {
        handleSetValidatedDevices({ uid: data.uid, sn: trimmedSn, errorMessage: "Device validation failed" }, index)
      }
    } catch (error) {
      console.error(error)
      handleSetValidatedDevices({ uid: data.uid, sn: trimmedSn, errorMessage: "Device validation failed" }, index)
    }
  }

  const handleChange = (data: UITypes.InvalidDevice) => {
    setValidatedDevices(prev => prev.map(item => (item.uid === data.uid ? { ...item, sn: data.sn } : item)))
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    const serialNumbers: string[] = validatedDevices.map(item => item.sn as string)

    try {
      await packWHManagerOrderItem({
        id: orderItem.id,
        serialNumbers: serialNumbers,
      }).unwrap()

      toast.success("Successfully packed")
      onSuccess()
    } catch (error) {
      const errorMessage = getErrorMessage(error as FetchBaseQueryError | SerializedError | undefined)
      if (errorMessage) {
        toast.error(errorMessage)
      }
    }
  }

  const checkSubmitAvailability = (): boolean => {
    if (validatedDevices?.length !== orderItem?.quantity) {
      return true
    }

    if (
      validatedDevices?.some(item => item?.sn === "" || item.errorMessage !== "" || !isValidDeviceSn(item.sn.trim()))
    ) {
      return true
    }
    return false
  }

  const handleExport = () => {
    exportPackingDevices(validatedDevices, order.id)
  }

  if (isCWA && orderItem) {
    return <CWAPackingHandler orderItem={orderItem} onCancel={onCancel} onSuccess={onSuccess} />
  }

  return (
    <>
      {createPortal(
        <ModalLayout
          width="560px"
          confirmButtonText="Save"
          confirmButtonType="submit"
          buttonSize="auto"
          onCancel={onCancel}
          onConfirm={onSubmit}
          isDisabled={checkSubmitAvailability()}
          isFetching={isPackingCWA}
          footerClassName={styles.exportButtonFirst}
          customButtons={
            <ExportButton onClick={handleExport} color="blueBordered" size="auto">
              <ExportIconStyled />
              Export
            </ExportButton>
          }
        >
          <ModalHeader title="Packing">
            <IconPacking />
          </ModalHeader>
          <div className={styles.modalContent}>
            <div className={styles.dataWrapper}>
              <div className={styles.dataItem}>
                <h3 className={styles.dataItemTitle}>Contractor&#39;s name</h3>
                <span className={styles.dataItemText}>{order.company_name}</span>
              </div>
              <div className={styles.dataItem}>
                <h3 className={styles.dataItemTitle}>Order Quantity</h3>
                <span className={styles.dataItemText}>{orderItem?.quantity}</span>
              </div>
            </div>
            <div className={styles.line}></div>
            <div className={classNames(styles.dataWrapper, styles.devicesInfo)}>
              <div className={styles.devicesInfoItem}>
                <h3 className={styles.dataItemTitle}>Added Thermostats</h3>
                <span className={styles.dataItemText}>{validatedDevices?.length}</span>
              </div>
            </div>
            {Boolean(validatedDevices?.length) &&
              validatedDevices.map((item, index) => (
                <InputSN
                  {...{ ...item, ...{ row: index + 1 } }}
                  key={item.uid}
                  onBlur={data => handleBlur(data, index)}
                  onDelete={onItemDelete}
                  onChange={handleChange}
                  autoFocus={validatedDevices?.length == index + 1 ? true : false}
                />
              ))}
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
